<template>
    <section
        :data-mui-theme="theme"
        class="slice slice-testimonials slice-testimonials--single-quote"
        aria-label="testimonial"
        data-testid="testimonials-single-quote-slice"
    >
        <div class="slice-content" :class="{'slice-content--reverse': isReverse}">
            <div>
                <div class="slice-testimonials--single-quote__quote" :class="{'slice-testimonials--single-quote__quote--reverse': isReverse}">
                    <svg width="66" height="53" viewBox="0 0 66 53" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path
                            d="M31.0256 0.902219L28.7692 8.04752C17.4872 4.85093 7.7094 14.6287 7.89744 28.1672C18.4274 23.2783 27.641 30.2356 27.641 39.2612C27.641 46.9706 22 52.4236 14.6667 52.4236C6.01709 52.4236 0 45.2783 0 30.9877C0 9.17572 13.1624 -3.6106 31.0256 0.902219ZM66 0.902219L63.9316 8.04752C52.4615 4.85093 42.8718 14.6287 42.8718 28.1672C53.5897 23.2783 62.6154 30.2356 62.6154 39.2612C62.6154 46.9706 57.1624 52.4236 49.641 52.4236C41.1795 52.4236 35.1624 45.2783 35.1624 30.9877C35.1624 9.17572 48.1368 -3.6106 66 0.902219Z"
                        />
                    </svg>
                    <PrismicRichText
                        v-if="isFilled.richText(slice.primary.quote)"
                        class="slice-testimonials--single-quote__quote-text"
                        :field="slice.primary.quote"
                        data-testid="testimonials-single-quote-quote"
                    />
                </div>
                <div class="slice-testimonials--single-quote__author-info--desktop">
                    <TestimonialsSingleQuoteAuthorInfo
                        :author="slice.primary.author"
                        :author-job="slice.primary.authorJob"
                        :logo="slice.primary.logo"
                    />
                </div>
            </div>
            <div class="slice-testimonials--single-quote__author-info-wrapper">
                <div v-if="hasImage" class="slice-testimonials--single-quote__author-image">
                    <MktCardHandAdaptive brand-theme>
                        <PrismicImage
                            :alt="slice.primary.image.alt || ''"
                            :field="slice.primary.image"
                            data-testid="testimonials-single-quote-image"
                        />
                    </MktCardHandAdaptive>
                </div>
                <div class="slice-testimonials--single-quote__author-info--mobile">
                    <TestimonialsSingleQuoteAuthorInfo
                        :author="slice.primary.author"
                        :author-job="slice.primary.authorJob"
                        :logo="slice.primary.logo"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {TestimonialsSlice} from '@prismicio-types';
    import {MktCardHandAdaptive} from '#components';
    import TestimonialsSingleQuoteAuthorInfo from '../components/TestimonialsSingleQuoteAuthorInfo.vue';

    type TestimonialsSingleQuoteInZone = Extract<TestimonialsSlice, {variation: 'testimonialsSingleQuote'}>;

    const props = defineProps(getSliceComponentProps<TestimonialsSingleQuoteInZone>());

    const isReverse = computed(() => props.slice.primary.reverse);
    const hasImage = computed(() => isFilled.image(props.slice.primary.image));
    const theme = computed(() => props.slice.primary.theme);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-testimonials--single-quote {
        .slice-content {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-9);
            text-align: left;

            &--reverse {
                flex-direction: column-reverse;
            }

            @include mq.screen_md(min) {
                display: grid;
                grid-template-columns: 1.4fr 0.8fr;
                place-items: center;
                padding-left: 140px;
                padding-right: 140px;
                gap: var(--joy-core-spacing-12);

                &--reverse {
                    grid-template-columns: 0.8fr 1.4fr;

                    > :first-child {
                        order: 2;
                    }

                    > :last-child {
                        order: 1;
                    }
                }
            }
        }

        &__quote-text {
            > :first-child {
                &:before {
                    content: '“';
                }
            }
            > :last-child {
                &:after {
                    content: '"';
                }
            }
        }

        &__quote {
            display: flex;
            flex-direction: column;
            color: var(--joy-color-neutral-70);

            @include mq.screen_md(min) {
                margin-bottom: var(--joy-core-spacing-9);
            }

            p {
                @include text.getFontBySize(primary-600);
            }

            svg {
                display: block;
                margin-bottom: var(--joy-core-spacing-8);

                path {
                    fill: var(--mui-color-background-darker);
                }
            }

            &--reverse {
                svg {
                    order: 1;
                    align-self: flex-end;
                    margin-bottom: 0;
                    margin-top: var(--joy-core-spacing-8);
                    transform: rotate(180deg);
                }
            }
        }

        &__author-info {
            &--desktop {
                display: none;
                @include mq.screen_md(min) {
                    display: block;
                }
            }
            &--mobile {
                display: block;
                @include mq.screen_md(min) {
                    display: none;
                }
            }
        }

        &__author-info-wrapper {
            display: flex;
            align-items: center;
            gap: var(--joy-core-spacing-8);
        }

        &__author-image {
            width: 200px;

            @include mq.screen_md(min) {
                width: 260px;
                max-width: 100%;
            }
        }
    }
</style>
